import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Card,
  IconButton,
  Button,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MUITypography from "../../Components/MUITypography";
import {
  API_GET_ROOMS_LIST,
  API_DELETE_ROOM,
  API_GET_ROOMSLOTS,
} from "../../Util/URLConstants";
import {
  align_center,
  align_column,
  align_left,
  align_row,
  VM_Colors,
} from "../../Styles/VMStyles";

import RoomSlots from "./RoomSlots";
import AddUpdateRooms from "./AddUpdateRooms";
import ReusableDrawer from "../../Components/ReusableDrawer";
import StorageFactory from "../../Util/StorageFactory";
import {
  ACCESS_RESTRICTED_MESSAGE,
  ROUTE_LOGIN,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  SESSION_USER_TYPE_KEY,
} from "../../Util/VMConstants";
import {
  formatAmount,
  isAdminLoggedIn,
  isEmpty,
  normalizeLoginPermissions,
} from "../../Util/UtilHelper";
import Navbar from "../../Menu/Navbar";
import NoPermission from "../../Components/NoPermission";

const RoomsInfo = ({ userState }) => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState({});
   const [loading, setLoading] = useState(false);
  const [drawerConfig, setDrawerConfig] = useState({
                                                open: false,
                                                title: "",
                                                content: null,
                                                showFooter: true,
                                              });
const [slotAlertOpen, setSlotAlertOpen] = useState(false);
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const sessionUserType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const userType = userState?.user_type || sessionUserType;
  // Merge profile + session permission IDs (profile can lag; session can be stale)
  const permissionIds = [
    ...new Set([
      ...normalizeLoginPermissions(userState?.permissions),
      ...normalizeLoginPermissions(
        sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS) || []
      ),
    ]),
  ];
  const isRegistrationFlow = isEmpty(userID);
  const hasRoomPermission = (permissionId) => {
    if (isRegistrationFlow) return true;
    if (permissionIds.includes(String(permissionId))) return true;
    // Venue Admin with no role permission list → full Room Details UI
    if (isEmpty(permissionIds) && isAdminLoggedIn(userType)) return true;
    return false;
  };
  const currencySymbol = userState.country_data?.currency_symbol || '₹';
  const canViewRooms = hasRoomPermission("VM016");
  const canAddRooms = hasRoomPermission("VM018");
  const canEditRooms = hasRoomPermission("VM022");
  const canDeleteRooms = hasRoomPermission("VM023");

  const fetchRooms = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${API_GET_ROOMS_LIST}?user_id=${userState.user_id}`);
      setRooms(response.data.grouped_rooms || {});
      setLoading(false)
    } catch (error) {
      console.error("Error fetching rooms:", error);
      setLoading(false)
    }
  }, [userState.user_id]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleDelete = async (room_id) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this room?");
  if (!confirmDelete) return;
    try {
      setLoading(true)
      await axios.get(`${API_DELETE_ROOM}?room_id=${room_id}`);
      setLoading(false)
      fetchRooms();
    } catch (error) {
      console.error("Error deleting room:", error);
      setLoading(false)
    }
  };

  const closeDrawer = () => setDrawerConfig((prev) => ({ ...prev, open: false }));

  const openAddRoomDrawer = () => {
    setDrawerConfig({
      open: true,
      title: "Add Rooms",
      content: (
        <AddUpdateRooms
          userId={userState.user_id}
          onSuccess={() => {
            fetchRooms();
            closeDrawer();
          }}
        />
      ),
      showFooter: false,
    });
  };

  const openEditRoomDrawer = (roomType, index) => {
    const room = rooms[roomType][index];
    setDrawerConfig({
      open: true,
      title: "Update Room",
      content: (
        <AddUpdateRooms
          userId={userState.user_id}
          roomData={room}
          onSuccess={() => {
            fetchRooms();
            closeDrawer();
          }}
        />
      ),
      showFooter: false,
    });
  };

  /** Prefill add-room form from an existing room; only room number must be entered again. */
  const openDuplicateRoomDrawer = (room) => {
    setDrawerConfig({
      open: true,
      title: "Duplicate Room",
      content: (
        <AddUpdateRooms
          userId={userState.user_id}
          roomData={room}
          isDuplicate
          onSuccess={() => {
            fetchRooms();
            closeDrawer();
          }}
        />
      ),
      showFooter: false,
    });
  };

  const openRoomSlotsDrawer = () => {
    setDrawerConfig({
      open: true,
      title: "Rooms Configuration",
      content: <RoomSlots 
       user_id={userState.user_id}
       onSuccess={() => {
            closeDrawer();
          }} />,
      showFooter: false,
    });
  };

const fetchTimeSlots = () => {
  setLoading(true);
  axios
    .get(`${API_GET_ROOMSLOTS}?user_id=${userState.user_id}`)
    .then((resp) => {
      setLoading(false);
      const respData = resp.data;

      if (respData.status_code === 200) {
        const slotData = respData.slots || [];

        if (slotData.length === 0) {
          // 🔔 Show alert dialog instead of opening drawer directly
          setSlotAlertOpen(true);
        } else {
          openAddRoomDrawer();
        }
      }
    })
    .catch((err) => {
      setLoading(false);
      console.error("Fetch TimeSlots Error:", err);
    });
};


  return (
    <Box >
      {
        !drawerConfig.open && isEmpty(userID) &&
        <Navbar
          registrationUser={{
            id: userState.user_id,
            name: userState.name,
            userType: userState.user_type,
            profilePic: userState.image_url,
          }}
        />
      }

     <Box p={3} sx={{backgroundColor:VM_Colors.white2,minHeight:'100vh'}}> 

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2  }}>
        <MUITypography label="Room Details"  variant="h3" gutterBottom   sx={{  ...align_left, fontWeight: 700, color: '#1E1E1E' }}/>

          {
            canAddRooms &&
            <Box sx={{ display: "flex", gap: 2 }}>  
              <Button variant="contained" startIcon={<AddIcon />} onClick={openRoomSlotsDrawer} sx={{width:250}} >
              Create Time Slots
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={fetchTimeSlots} sx={{width:200}}>
                Add Rooms
              </Button>
            </Box>    
          }
      </Box>

      {!canViewRooms ? (
        <Box sx={{ mt: 4 }}>
          <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />
        </Box>
      ) : loading ? (
        <Box sx={{ ...align_center, py: 8 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(rooms).length === 0 ? (
        <MUITypography variant="body1" sx={{ m: 5, ...align_center }}>
          No rooms found.
        </MUITypography>
      ) : (
        <Box sx={{ maxWidth: "75vw" }}>
          {Object.entries(rooms).map(([roomType, roomList]) => (
            <Box key={roomType} sx={{ mt: 3 }}>
              <Typography variant="h3" sx={{ mb: 2 }}>{roomType}</Typography>
              <Box sx={{
                display: "flex",
                overflowX: "auto",
                gap: 2,
                pb: 1,
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: 4 },
              }}>
                {roomList.map((room, index) => (
                  <Card key={room.room_id} elevation={4} sx={{
                    minWidth: "150px", p: 2, borderRadius: 2,
                    "&:hover": { transform: "scale(1.02)", boxShadow: 6 }
                  }}>
                    <Box sx={{ ...align_row, justifyContent: "space-between" }}>
                      <Avatar alt="Room" src={'/static/room.svg'} sx={{ width: 24, height: 24 }} />
                      <Box sx={{ px: 2, ...align_column }}>
                        <MUITypography>{room.room_name}</MUITypography>
                        <MUITypography>{room.room_type}</MUITypography>
                        <MUITypography sx={{ color: VM_Colors.primary, fontWeight: 600, pt: 1 }}>
                          {formatAmount( room.room_cost,currencySymbol)}
                        </MUITypography>
                      </Box>
                      <Box sx={{ ...align_column, alignItems: "center" }}>
                        {canDeleteRooms && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(room.room_id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        )}

                        {canEditRooms && (
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openEditRoomDrawer(roomType, index)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        )}

                        {canAddRooms && (
                          <Tooltip title="Duplicate — reuse details with a new room number">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openDuplicateRoomDrawer(room)}
                              aria-label="Duplicate room"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

        <ReusableDrawer
        open={drawerConfig.open}
        title={drawerConfig.title}
        onClose={closeDrawer}
        showFooter={drawerConfig.showFooter}
      >
        {drawerConfig.content}
        </ReusableDrawer>

      {isEmpty(userID) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 6,
            pb: 4,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(`/${ROUTE_LOGIN}`, { replace: true })}
            sx={{
              bgcolor: VM_Colors.primary,
              color: "#ffffff",
              px: 5,
              py: 1.5,
              minWidth: 250,
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                bgcolor: VM_Colors.primary,
                opacity: 0.9,
              },
            }}
          >
            Click Here to Login
          </Button>
        </Box>
      )}

      </Box>

<Dialog
  open={slotAlertOpen}
  onClose={() => setSlotAlertOpen(false)}
>
  <DialogTitle>Alert</DialogTitle>

  <DialogContent>
    <MUITypography>
      There are no timeslots exist, please update the slots.
    </MUITypography>
  </DialogContent>

  <DialogActions>
    {/* <Button
      onClick={() => setSlotAlertOpen(false)}
      color="inherit"
    >
      Cancel
    </Button> */}

    <Button
      variant="contained"
      onClick={() => {
        setSlotAlertOpen(false);
        openRoomSlotsDrawer();
      }}
    >
      OK
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  );
};

export default RoomsInfo;
